// ─────────────────────────────────────────────────────────────────────────────
// V2Expenses — /dashboard/expenses in the v2 design.
// Endpoints are v1's: GET /api/expenses?year&month, GET /api/expenses/summary?year&month,
// GET /api/expenses/summary/year?year, POST /api/expenses { category, description, amount, expenseDate },
// DELETE /api/expenses/:id. The server has no update route, so expenses are add/delete only.
// Categories are the model's enum; each has a fixed colour (colour follows the category, never rank).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Sheet, Skel, inr } from "./primitives";
import { Field, PageHead } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CATS = [
  { k: "Rent", c: "#2a78d6", g: "building" },
  { k: "Utilities", c: "#eb6834", g: "pulse" },
  { k: "Salaries", c: "#1baf7a", g: "people" },
  { k: "Travel", c: "#eda100", g: "truck" },
  { k: "Marketing", c: "#e87ba4", g: "sparkle" },
  { k: "Supplies", c: "#008300", g: "box" },
  { k: "Maintenance", c: "#6250d6", g: "repeat" },
  { k: "Miscellaneous", c: "#e34948", g: "note" },
];
const CAT = Object.fromEntries(CATS.map((x) => [x.k, x]));
const pad = (n) => String(n).padStart(2, "0");
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const dayKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };
const dayLabel = (k) => {
  const [y, m, d] = k.split("-").map(Number); const dt = new Date(y, m - 1, d);
  const t = new Date(); const diff = Math.round((new Date(t.getFullYear(), t.getMonth(), t.getDate()) - dt) / 864e5);
  if (diff === 0) return "Today"; if (diff === 1) return "Yesterday";
  return dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};
async function get(path) { const r = await fetch(`${API_BASE}${path}`, { headers: auth() }); if (!r.ok) throw new Error("Couldn't load expenses"); return r.json(); }

export default function V2Expenses({ showAlert }) {
  usePageTitle("Expenses");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("expenses", "write");
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [list, setList] = useState(null);
  const [prevTotal, setPrevTotal] = useState(null);
  const [yearTotal, setYearTotal] = useState(null);
  const [err, setErr] = useState("");
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const addBtn = useRef(null);

  const load = useCallback(async () => {
    setErr(""); setList(null);
    const prev = new Date(ym.y, ym.m - 2, 1);
    try {
      const [rows, ps, yr] = await Promise.all([
        get(`/api/expenses?year=${ym.y}&month=${ym.m}`),
        get(`/api/expenses/summary?year=${prev.getFullYear()}&month=${prev.getMonth() + 1}`).catch(() => null),
        get(`/api/expenses/summary/year?year=${ym.y}`).catch(() => null),
      ]);
      setList(Array.isArray(rows) ? rows : []); setPrevTotal(ps?.totalExpenses ?? null); setYearTotal(yr?.totalExpenses ?? null);
    } catch (e) { setErr(e.message); setList([]); }
  }, [ym]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCat(null); }, [ym]);

  const all = list || [];
  const total = all.reduce((a, e) => a + (e.amount || 0), 0);
  const byCat = CATS.map((c) => ({ ...c, v: all.filter((e) => e.category === c.k).reduce((a, e) => a + e.amount, 0), n: all.filter((e) => e.category === c.k).length })).filter((c) => c.v > 0);
  const delta = prevTotal ? ((total - prevTotal) / prevTotal) * 100 : null;
  const isCur = ym.y === now.getFullYear() && ym.m === now.getMonth() + 1;
  const daysIn = isCur ? now.getDate() : new Date(ym.y, ym.m, 0).getDate();
  const largest = all.reduce((a, e) => (!a || e.amount > a.amount ? e : a), null);
  const shown = all.filter((e) => (!cat || e.category === cat) && (!q.trim() || `${e.description} ${e.category}`.toLowerCase().includes(q.trim().toLowerCase())));
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach((e) => { const k = dayKey(e.expenseDate); if (!m.has(k)) m.set(k, []); m.get(k).push(e); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [shown]);
  const step = (dir) => setYm(({ y, m }) => { const d = new Date(y, m - 1 + dir, 1); return { y: d.getFullYear(), m: d.getMonth() + 1 }; });
  const [hi, setHi] = useState(null);

  return (
    <div className="v2p v2ex">
      <PageHead eyebrow="Expenses" title="Expenses" sub="Day-to-day spending — rent, utilities, travel and the rest."
        actions={canWrite && <button ref={addBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setAdding(true)}><Glyph name="plus" size={17} stroke={2.2} /> Add expense</button>} />
      <div className="v2i-toolbar v2ex-toolbar">
        <div className="v2-stepper v2-glass" role="group" aria-label="Month">
          <button type="button" aria-label="Previous month" onClick={() => step(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
          <span className="v2-stepper-label">{MONTHS[ym.m - 1]} {ym.y}</span>
          <button type="button" aria-label="Next month" disabled={isCur} onClick={() => step(1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
        </div>
        {!isCur && <button type="button" className="v2k-link" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() + 1 })}>Back to this month</button>}
      </div>

      {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}

      <section className="v2-card v2ex-hero">
        {!list ? <Skel h={150} r={22} /> : <>
          <div className="v2ex-hero-top">
            <div className="v2ex-hero-main">
              <span className="v2rp-kicker">Spent in {MONTHS[ym.m - 1]}</span>
              <strong className="v2rp-big">{inr(total)}</strong>
              <span className="v2rp-hero-sub">
                {delta == null ? `${all.length} expense${all.length === 1 ? "" : "s"}`
                  : <><span className={`v2ex-delta ${delta > 0 ? "is-up" : "is-down"}`}><Glyph name="arrowUpRight" size={13} stroke={2.2} />{Math.abs(delta).toFixed(0)}%</span> {delta > 0 ? "more" : "less"} than {MONTHS[(ym.m + 10) % 12]} ({inr(prevTotal)})</>}
              </span>
            </div>
            <div className="v2rp-hero-side">
              <div><span>Per day</span><b>{inr(total / daysIn)}</b></div>
              <div><span>Largest</span><b>{largest ? inr(largest.amount) : "—"}</b></div>
              <div><span>{ym.y} so far</span><b>{yearTotal == null ? "—" : inr(yearTotal)}</b></div>
            </div>
          </div>
          {byCat.length > 0 && <>
            <div className="v2ex-bar" onPointerLeave={() => setHi(null)}>
              {byCat.map((c) => <button key={c.k} type="button" aria-label={`${c.k} ${inr(c.v)}`} style={{ flexGrow: c.v, background: c.c }}
                className={(hi || cat) && (hi || cat) !== c.k ? "is-dim" : ""} onPointerEnter={() => setHi(c.k)} onClick={() => setCat(cat === c.k ? null : c.k)} />)}
            </div>
            <div className="v2ex-cats">
              {[...byCat].sort((a, b) => b.v - a.v).map((c) => (
                <button key={c.k} type="button" className={`v2ex-cat${cat === c.k ? " is-on" : ""}${cat && cat !== c.k ? " is-dim" : ""}`} aria-pressed={cat === c.k}
                  onPointerEnter={() => setHi(c.k)} onPointerLeave={() => setHi(null)} onClick={() => setCat(cat === c.k ? null : c.k)}>
                  <i style={{ background: c.c }} /><span>{c.k}</span><b>{inr(c.v)}</b><em>{Math.round((c.v / total) * 100)}%</em>
                </button>))}
            </div>
          </>}
        </>}
      </section>

      <div className="v2i-toolbar v2ex-filter">
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description or category" aria-label="Search expenses" /></div>
        {cat && <button type="button" className="v2-chip-btn v2i-preset is-on" onClick={() => setCat(null)}><i className="v2ex-dot" style={{ background: CAT[cat].c }} /> {cat} <Glyph name="close" size={13} stroke={2.2} /></button>}
      </div>

      <section className="v2-card v2ex-list">
        {!list ? <div className="v2-list">{[0, 1, 2, 3].map((i) => <Skel key={i} h={56} r={16} />)}</div>
          : !shown.length ? <div className="v2c-empty"><Duo name="wallet" size={48} /><strong>{all.length ? "Nothing matches" : `No expenses in ${MONTHS[ym.m - 1]}`}</strong><span>{all.length ? "Try another search or clear the category." : "Record rent, bills, travel and other spending to see where money goes."}</span>
            {!all.length && canWrite && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setAdding(true)}>Add expense</button>}</div>
          : groups.map(([k, rows]) => (
            <div key={k} className="v2ex-day">
              <div className="v2ex-day-head"><span>{dayLabel(k)}</span><b>{inr(rows.reduce((a, e) => a + e.amount, 0))}</b></div>
              {rows.map((e) => { const c = CAT[e.category] || CATS[7]; return (
                <div key={e._id} className="v2ex-row">
                  <span className="v2ex-icon"><Duo name={c.g} size={28} /></span>
                  <span className="v2-row-main"><span className="v2-row-title">{e.description}</span><span className="v2-row-sub">{e.category}</span></span>
                  <b className="v2ex-amt">{inr(e.amount, { decimals: e.amount % 1 ? 2 : 0 })}</b>
                  {canWrite && <MenuButton className="v2-icon-btn v2i-more" label={`Options for ${e.description}`} width={200} items={[{ label: "Delete", glyph: "close", danger: true, onSelect: () => setRemoving(e) }]} />}
                </div>); })}
            </div>))}
      </section>

      <AddExpense open={adding} originRef={addBtn} ym={ym} onClose={() => setAdding(false)}
        onSaved={(e, again) => { showAlert?.(`${e.description} added`, "success"); if (!again) setAdding(false); load(); }} />
      <Sheet open={!!removing} onClose={() => setRemoving(null)} width={420} title="Delete this expense?" subtitle={removing ? `${removing.description} · ${inr(removing.amount)}` : ""}
        footer={<><button type="button" className="v2-btn" onClick={() => setRemoving(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try { const r = await fetch(`${API_BASE}/api/expenses/${removing._id}`, { method: "DELETE", headers: auth() }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || "Couldn't delete"); showAlert?.("Expense deleted", "success"); setRemoving(null); load(); }
            catch (x) { setRemoving((v) => ({ ...v, error: x.message })); }
          }}>Delete</button></>}>
        <p className="v2c-remove-text">It will also drop out of your reports and profit & loss. Expenses can't be edited, so delete and re-add to correct one.</p>
        {removing?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {removing.error}</div>}
      </Sheet>
    </div>
  );
}

function AddExpense({ open, originRef, ym, onClose, onSaved }) {
  const blank = () => {
    const n = new Date(); const cur = ym.y === n.getFullYear() && ym.m === n.getMonth() + 1;
    return { amount: "", category: "", description: "", expenseDate: cur ? today() : `${ym.y}-${pad(ym.m)}-01` };
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const amtRef = useRef(null);
  useEffect(() => { if (open) { setF(blank()); setErr(""); } }, [open]); // eslint-disable-line
  const save = async (again) => {
    setErr("");
    const amount = parseFloat(f.amount);
    if (!(amount > 0)) { setErr("Enter an amount greater than 0."); return; }
    if (!f.category) { setErr("Choose a category."); return; }
    if (!f.description.trim()) { setErr("Add a short description."); return; }
    if (!f.expenseDate) { setErr("Pick a date."); return; }
    setBusy(true);
    try {
      const body = { category: f.category, description: f.description.trim(), amount, expenseDate: f.expenseDate };
      const r = await fetch(`${API_BASE}/api/expenses`, { method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || "Couldn't save");
      onSaved(body, again);
      if (again) { setF((p) => ({ ...blank(), category: p.category, expenseDate: p.expenseDate })); setTimeout(() => amtRef.current?.focus(), 50); }
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} originRef={originRef} width={520} title="Add expense"
      footer={<><button type="button" className="v2-btn" onClick={() => save(true)} disabled={busy}>Save &amp; add another</button>
        <button type="submit" form="v2ex-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button></>}>
      <form id="v2ex-form" className="v2-form" onSubmit={(e) => { e.preventDefault(); save(false); }}>
        <div className="v2ex-amount">
          <span>₹</span>
          <input ref={amtRef} autoFocus inputMode="decimal" style={{ width: `${Math.max(1, String(f.amount || "0").length) * 0.62 + 0.35}em` }} type="number" min="0" step="0.01" placeholder="0" aria-label="Amount" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} />
        </div>
        <Field label="Category">
          <div className="v2ex-pick" role="radiogroup" aria-label="Category">
            {CATS.map((c) => (
              <button key={c.k} type="button" role="radio" aria-checked={f.category === c.k} className={`v2ex-pick-btn${f.category === c.k ? " is-on" : ""}`}
                onClick={() => setF((p) => ({ ...p, category: c.k }))}>
                <span className="v2ex-pick-ico"><Duo name={c.g} size={30} /></span>{c.k}
              </button>))}
          </div>
        </Field>
        <Field label="Description"><input className="v2-input" value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} placeholder="e.g. Office electricity — October" /></Field>
        <Field label="Date"><input className="v2-input" type="date" max={today()} value={f.expenseDate} onChange={(e) => setF((p) => ({ ...p, expenseDate: e.target.value }))} /></Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
